import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import BaseApi from "../services/base-api";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "./ToastContext";
import { User } from "../types/User";
import { useTranslation } from "react-i18next";
import { normalizePermissions } from "../utils/permissions";

/* ===================== TYPES ===================== */

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  otpRequired: boolean;
  phoneNumber: string | null;
  permissions: string[];
  logout: () => void;
  setAuthData: (token: string, user: User, perms?: any) => void;
  updateUser: (user: Partial<User>) => void;
  sendPhoneOtp: (
    phone: string,
    phoneCode: string,
    otpChannel?: "sms" | "whatsapp",
  ) => Promise<any>;
  verifyPhoneOtp: (
    otp: string,
    phone: string,
    phoneCode: string,
  ) => Promise<any>;
  resendPhoneOtp: (
    phone: string,
    phoneCode: string,
    otpChannel?: "sms" | "whatsapp",
  ) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* ===================== PROVIDER ===================== */

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { success } = useToast();
  const { t } = useTranslation();

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [otpRequired, setOtpRequired] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);

  /** 🔔 Logout modal state */
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  /* ===================== HELPERS ===================== */

  const hasWildcardPermission = (perms: string[]) =>
    perms.includes("*") || perms.includes("all") || perms.includes("ALL");

  /**
   * Extract permissions from various formats:
   * 1. { "all": ["*"] }
   * 2. ["*", "view_users"]
   * 3. { "users": ["view_users"], "products": ["view_products"] }
   */
  const extractPermissions = (permissionsData: any): string[] =>
    normalizePermissions(permissionsData);

  const fetchRolePermissions = async (roleId: string): Promise<string[]> => {
    try {
      // console.log("[AUTH] 🔄 Fetching permissions for roleId:", roleId);
      const response = await BaseApi.get(`/roles/${roleId}/permissions`);
      const permissionsPayload =
        response.data?.permissions ?? response.data ?? response.permissions;
      const perms = extractPermissions(permissionsPayload);
      // console.log("[AUTH] ✅ Fetched role permissions:", perms);
      return perms;
    } catch (error) {
      console.error("[AUTH] ❌ Failed to fetch role permissions:", error);
      return [];
    }
  };

  /* ===================== INIT ===================== */

  const initializeAuth = useCallback(async () => {
    // console.log("[AUTH] 🚀 Initializing auth...");
    setIsLoading(true);
    try {
      const storedToken =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const storedUser = localStorage.getItem("userData");
      const storedRawPermissions = localStorage.getItem("userPermissionsRaw");
      const storedPermissions = localStorage.getItem("userPermissions");

      // console.log("[AUTH] Stored token:", storedToken ? "exists" : "none");
      // console.log("[AUTH] Stored user:", storedUser ? "exists" : "none");
      // console.log("[AUTH] Stored permissions:", storedPermissions ? "exists" : "none");

      if (!storedToken) {
        // console.log("[AUTH] ❌ No token found");
        return;
      }

      setToken(storedToken);

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // console.log("[AUTH] 📦 Parsed user:", parsedUser);
        setUser(parsedUser);

        if (parsedUser.roleId) {
          const perms = await fetchRolePermissions(parsedUser.roleId);
          if (perms.length > 0) {
            const enrichedUser = { ...parsedUser, permissions: perms };
            setPermissions(perms);
            setUser(enrichedUser);
            localStorage.setItem("userData", JSON.stringify(enrichedUser));
            localStorage.setItem("userPermissions", JSON.stringify(perms));
            localStorage.setItem("userPermissionsRaw", JSON.stringify(perms));
            return;
          }
        }

        // Fallback to stored raw permissions if live fetch is unavailable
        if (storedRawPermissions) {
          try {
            const parsedRawPerms = JSON.parse(storedRawPermissions);
            const extractedPerms = extractPermissions(parsedRawPerms);
            setPermissions(extractedPerms);
            setUser((prev) =>
              prev ? { ...prev, permissions: extractedPerms } : null,
            );
            return;
          } catch (e) {
            console.warn("[AUTH] ⚠️ Failed to parse stored raw permissions:", e);
          }
        }

        // Fallback to stored normalized permissions next
        if (storedPermissions) {
          try {
            const parsedPerms = JSON.parse(storedPermissions);
            const extractedPerms = extractPermissions(parsedPerms);
            // console.log("[AUTH] ✅ Restored permissions from storage:", extractedPerms);
            setPermissions(extractedPerms);
            setUser((prev) =>
              prev ? { ...prev, permissions: extractedPerms } : null,
            );
            return;
          } catch (e) {
            console.warn("[AUTH] ⚠️ Failed to parse stored permissions:", e);
          }
        }
      }
    } catch (error) {
      console.error("[AUTH] ❌ Init error:", error);
    } finally {
      setIsLoading(false);
      // console.log("[AUTH] ✅ Init complete");
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /* ===================== AUTH ACTIONS ===================== */

  const setAuthData = async (
    newToken: string,
    newUser: User,
    apiPermissions?: any,
  ) => {
    // console.group("[AUTH] 🔐 Setting auth data");
    // console.log("Token:", newToken ? "provided" : "missing");
    // console.log("User:", newUser);
    // console.log("API Permissions:", apiPermissions);

    setToken(newToken);
    localStorage.setItem("authToken", newToken);
    localStorage.setItem("token", newToken);

    let perms: string[] = [];

    // Priority 1: Use permissions passed directly (from API response)
    if (apiPermissions) {
      // console.log("[AUTH] Using API permissions");
      perms = extractPermissions(apiPermissions);
    }
    // Priority 2: Use permissions from user object
    else if (newUser.permissions) {
      // console.log("[AUTH] Using user.permissions");
      perms = extractPermissions(newUser.permissions);
    }
    // Priority 3: Fetch from role
    else if (newUser.roleId) {
      // console.log("[AUTH] Fetching permissions from roleId:", newUser.roleId);
      perms = await fetchRolePermissions(newUser.roleId);
    }

    // console.log("[AUTH] 🎯 Final permissions:", perms);
    // console.log("[AUTH] Has wildcard?", hasWildcardPermission(perms));

    const enrichedUser = { ...newUser, permissions: perms };
    setUser(enrichedUser);
    setPermissions(perms);

    // Store both user and permissions separately
    localStorage.setItem("userData", JSON.stringify(enrichedUser));
    localStorage.setItem(
      "userPermissionsRaw",
      JSON.stringify(apiPermissions ?? newUser.permissions ?? null),
    );
    localStorage.setItem("userPermissions", JSON.stringify(perms));

    console.groupEnd();
  };

  const login = async (email: string, password: string) => {
    // console.log("[AUTH] 🔑 Logging in with email:", email);
    const res = await BaseApi.post("/auth/login", { email, password });
    // console.log("[AUTH] Login response:", res.data);

    // Pass permissions from response if available
    await setAuthData(res.data.token, res.data.user, res.data.permissions);
  };

  const updateUser = (partialUser: Partial<User>) => {
    setUser((prevUser) => {
      if (!prevUser) return prevUser;

      const mergedUser = {
        ...prevUser,
        ...partialUser,
        permissions:
          partialUser.permissions || prevUser.permissions || permissions,
      };

      localStorage.setItem("userData", JSON.stringify(mergedUser));
      return mergedUser;
    });
  };

  const getOtpSentToastKey = (
    otpChannel: "sms" | "whatsapp" = "whatsapp",
  ) =>
    otpChannel === "sms"
      ? "OTP sent by SMS!"
      : "OTP sent to your WhatsApp!";

  const getOtpResentToastKey = (
    otpChannel: "sms" | "whatsapp" = "whatsapp",
  ) =>
    otpChannel === "sms"
      ? "OTP resent by SMS!"
      : "OTP resent to your WhatsApp!";

  const sendPhoneOtp = async (
    phone: string,
    phoneCode: string,
    otpChannel: "sms" | "whatsapp" = "whatsapp",
  ) => {
    // console.log("[AUTH] 📱 Sending OTP to:", phone);
    const res = await BaseApi.post("/auth/auth-user", { phone, phoneCode });
    if (!res.success) throw new Error();
    setOtpRequired(true);
    setPhoneNumber(phone);
    success(t(getOtpSentToastKey(otpChannel)));
    return res;
  };

  const verifyPhoneOtp = async (
    otp: string,
    phone: string,
    phoneCode: string,
  ) => {
    // console.group("[AUTH] 🔐 Verifying OTP");
    // console.log("Phone:", phone);

    const res = await BaseApi.post("/auth/verify-otp", {
      otp,
      phone,
      phoneCode,
    });

    // console.log("[AUTH] 📦 Full API response:", res);
    // console.log("[AUTH] User:", res.data.user);
    // console.log("[AUTH] Token:", res.data.token ? "exists" : "missing");
    // console.log("[AUTH] Permissions:", res.data.permissions);
    console.groupEnd();

    // IMPORTANT: Pass permissions from data.permissions (not data.user.permissions)
    await setAuthData(
      res.data.token,
      res.data.user,
      res.data.permissions, // This is the key fix!
    );

    setOtpRequired(false);
    setPhoneNumber(null);
    return res;
  };

  const resendPhoneOtp = async (
    phone: string,
    phoneCode: string,
    otpChannel: "sms" | "whatsapp" = "whatsapp",
  ) => {
    // console.log("[AUTH] 🔄 Resending OTP to:", phone);
    await BaseApi.post("/auth/resend-otp", { phone, phoneCode });
    success(t(getOtpResentToastKey(otpChannel)));
  };

  const refreshUser = async () => {};
  const refreshPermissions = async () => {};

  const hasPermission = (p: string) => {
    const result =
      hasWildcardPermission(permissions) || permissions.includes(p);
    // console.log(
    //   `[AUTH] hasPermission("${p}"):`,
    //   result,
    //   "| permissions:",
    //   permissions,
    // );
    return result;
  };

  const hasAnyPermission = (ps: string[]) => {
    const result =
      hasWildcardPermission(permissions) ||
      ps.some((p) => permissions.includes(p));
    // console.log(
    //   `[AUTH] hasAnyPermission([${ps.join(", ")}]):`,
    //   result,
    //   "| permissions:",
    //   permissions,
    // );
    return result;
  };

  const hasAllPermissions = (ps: string[]) => {
    const result =
      hasWildcardPermission(permissions) ||
      ps.every((p) => permissions.includes(p));
    // console.log(
    //   `[AUTH] hasAllPermissions([${ps.join(", ")}]):`,
    //   result,
    //   "| permissions:",
    //   permissions,
    // );
    return result;
  };

  /* ===================== LOGOUT ===================== */

  const performLogout = () => {
    // console.log("[AUTH] 🚪 Logging out");
    setToken(null);
    setUser(null);
    setOtpRequired(false);
    setPhoneNumber(null);
    setPermissions([]);

    localStorage.clear();

    setShowLogoutModal(false);

    if (location.pathname === "/" || location.pathname === "/home") {
      window.location.reload();
    } else {
      navigate("/home", { replace: true });
    }
  };

  const logout = () => {
    setShowLogoutModal(true);
  };

  /* 🔑 ESC + scroll lock */
  useEffect(() => {
    if (!showLogoutModal) return;

    document.body.style.overflow = "hidden";

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowLogoutModal(false);
    };

    window.addEventListener("keydown", handleEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEsc);
    };
  }, [showLogoutModal]);

  /* ===================== DEBUG ON MOUNT ===================== */
  // useEffect(() => {
  //   if (user) {
  //     console.group("[AUTH] 👤 Current Auth State");
  //     console.log("User:", user);
  //     console.log("Token:", token ? "exists" : "none");
  //     console.log("Permissions:", permissions);
  //     console.log("Has wildcard?", hasWildcardPermission(permissions));
  //     console.log("Is authenticated?", !!token);
  //     console.groupEnd();
  //   }
  // }, [user, token, permissions]);

  /* ===================== CONTEXT VALUE ===================== */

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    otpRequired,
    phoneNumber,
    permissions,
    logout,
    setAuthData,
    updateUser,
    sendPhoneOtp,
    verifyPhoneOtp,
    resendPhoneOtp,
    login,
    refreshUser,
    refreshPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* ===================== LOGOUT MODAL ===================== */}
      {showLogoutModal && (
        <div style={backdropStyle} onClick={() => setShowLogoutModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={headerStyle}>
              <h3 style={{ margin: 0, color: "#fff" }}>{t("Confirm Logout")}</h3>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={closeBtnStyle}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={bodyStyle}>
              <p style={{ margin: 0, color: "#374151", fontSize: 15 }}>
                {t("logout-message")}
              </p>
            </div>

            {/* Footer */}
            <div style={footerStyle}>
              <button
                onClick={() => setShowLogoutModal(false)}
                style={cancelBtnStyle}
              >
                {t("Cancel")}
              </button>

              <button onClick={performLogout} style={logoutBtnStyle}>
                {t("Logout")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

/* ===================== STYLES ===================== */

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 14,
  width: 420,
  maxWidth: "90%",
  overflow: "hidden",
  boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
  animation: "fadeInScale 0.2s ease-out",
};

const headerStyle: React.CSSProperties = {
  background: "red", // 🔴 red header
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#fff",
  fontSize: 18,
  cursor: "pointer",
};

const bodyStyle: React.CSSProperties = {
  padding: "20px",
};

const footerStyle: React.CSSProperties = {
  padding: "16px 20px",
  display: "flex",
  justifyContent: "flex-end",
  gap: 12,
  borderTop: "1px solid #e5e7eb",
};

const cancelBtnStyle: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "gray",
  cursor: "pointer",
};

const logoutBtnStyle: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 8,
  border: "none",
  background: "red", // 🔴 red logout
  color: "#fff",
  fontWeight: 500,
  cursor: "pointer",
};

/* ===================== HOOK ===================== */

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
